import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  Typography,
} from "@mui/material";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import { useSelector } from "react-redux";
import getAxiosError from "../../app/api/axiosError";
import { toast } from "react-toastify";
import { updateUserTokenToInvalid } from "../../app/features/login/loginSlice";
import { UserResponse } from "../../app/models/responseModels/userResponse";
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table";
import CommandOpsDataGrid from "../../components/common/commandOpsDataGrid/CommandOpsDataGrid";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import CachedIcon from "@mui/icons-material/Cached";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import { PAGES } from "../../enums/PagesEnum";
import {
  DownloadFileModel,
  convertArrayToCSV,
  downloadCsv,
  downloadXlsx,
  prepareAndDownloadFile,
} from "../../helpers/downloadFile";
import {
  useSubmitModal,
  useSubmitModalWithParameter,
} from "../../components/common/modalPopup/useSubmitModal";
import * as XLSX from "xlsx";
import { FdpResponse } from "../../app/models/responseModels/loginResponse";
import { getfdpUsersList } from "../../app/features/fdp/fdpSlice";
import {
  USER_COLUMN_NAME,
  USER_LABEL_NAME,
  UserExpectedColumns,
} from "../user/UserConstants";
import UserRolesTooltip from "../user/UserRolesTooltip";
import UserParcelCenterTooltip from "../user/UserParcelCenterTooltip";
import AddUserModal from "../user/modals/AddUserModal";
import EditUserModal from "../user/modals/EditUserModal";
import {
  addBulkUsers,
  updateUserStatus,
} from "../../app/features/user/userSlice";
import UserOneTimePasswordButton from "../user/UserOneTimePasswordButton";
import FileUploadModal from "../../components/common/modalPopup/FileUploadModal";
import { BulkUserRequest } from "../../app/models/requestModels/userRequest";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import UserActivationPrompt from "../user/modals/UserActivationPrompt";

function BulkUserAddRequestModel(users: any[]): BulkUserRequest {
  return {
    users: users,
  };
}

interface FdpUsersProps {
  fdpRowData: FdpResponse;
}
const FdpUsers = ({ fdpRowData }: FdpUsersProps) => {
  const dispatch = useAppDispatch();
  const fdpId = fdpRowData.id;
  const { isFdpLoading, fdpUsersList } = useSelector(
    (state: RootState) => state.fdp
  );
  const { isLoading } = useSelector((state: RootState) => state.user);

  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.USER
    )
  );
  const IsFdcAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
          "Administrators"
        )
      : false
  );
  const IsCreatePermission = pagePermission
    ? pagePermission.isCreatePermission
    : false;
  // ***** States *****
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [tableData, setTableData] = useState<UserResponse[]>([]);
  const [selectedRows, setSelectedRows] = useState<any>([]);
  const [isTableLoading, setTableLoading] = useState<boolean>(false);
  const [activeUsers, setActiveUsers] = useState<UserResponse[]>([]);
  const [isActiveUserView, setActiveUserView] = useState<boolean>(true);
  const [tableDataCount, setTableDataCount] = useState<string>("");
  const [openUserActivationPropmtModal, setOpenUserActivationPropmtModal] =
    useState<boolean>(false);

  const getFdpUsersData = useCallback(
    async (fdpId: number) => {
      setTableLoading(true);
      try {
        const response = await dispatch(getfdpUsersList(fdpId)).unwrap();
        if (response.isSuccess) {
          setTableLoading(false);
        }
        if (!response.isSuccess) {
          setTableLoading(false);
        }
      } catch (error) {
        setTableLoading(false);
        const appError = getAxiosError(error);
        toast.error(appError.errorMessage, {
          toastId: "fdpUsersListException",
        });
      }
    },
    [dispatch]
  );

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan && fdpUsersList.length === 0) {
      getFdpUsersData(fdpId);
    }
    return () => {
      isFunctionRan = false;
    };
  }, [getFdpUsersData, fdpId, fdpUsersList.length]);

  useEffect(() => {
    if (fdpUsersList.length > 0) {
      const activeUsers = fdpUsersList.filter(
        (user) => user.deleted === "false"
      );
      setActiveUsers(activeUsers);
    }
  }, [fdpUsersList]);

  useEffect(() => {
    if (fdpUsersList.length > 0) {
      if (isActiveUserView) {
        setTableData(activeUsers);
        setTableDataCount(activeUsers.length.toLocaleString());
      } else if (!isActiveUserView) {
        setTableData(fdpUsersList);
        setTableDataCount(fdpUsersList.length.toLocaleString());
      }
    }
  }, [fdpUsersList, isActiveUserView, activeUsers]);

  useEffect(() => {
    if (
      selectedRows.length > 0 &&
      selectedRows.some(
        (row: any) => row.original.deleted !== selectedRows[0].original.deleted
      )
    ) {
      toast.info("Either select Active or Inactive Rows!", {
        toastId: "mixedSelectionException",
      });
    }
  }, [selectedRows]);

  // ***** Handlers *****
  const handleRefresh = () => {
    getFdpUsersData(fdpId);
  };
  const handleResetAndRefresh = (table: MRT_TableInstance<UserResponse>) => {
    table.resetRowSelection(true);
    getFdpUsersData(fdpId);
  };

  const handleDeleteStatus = async (
    table: MRT_TableInstance<UserResponse>,
    deleteStatus: boolean,
    activationPeriod?: number
  ) => {
    let selectedRowIds: string[] = [];
    table.getSelectedRowModel().flatRows.forEach((row) => {
      selectedRowIds.push(row.original.id);
    });
    updateDeleteStatus(selectedRowIds, deleteStatus, table, activationPeriod);
  };

  const handleUserActivationValidation = (
    table: MRT_TableInstance<UserResponse>
  ) => {
    let shouldShowFilePopup = false;
    table.getSelectedRowModel().flatRows.forEach((row) => {
      // Check if any of the user's documents are mandatory and not uploaded
      const hasUnuploadedMandatoryDocs = row.original.userRelatedDocs.some(
        (doc) => doc.isMandatory && !doc.isUploaded
      );
      if (hasUnuploadedMandatoryDocs) {
        shouldShowFilePopup = true;
      }
    });
    if (IsFdcAdmin && shouldShowFilePopup) {
      setOpenUserActivationPropmtModal(true);
    } else {
      // Proceed with updating delete status if no conditions are met
      handleDeleteStatus(table, false);
    }
  };
  // ***** Handlers *****
  const updateDeleteStatus = async (
    selectedRowIds: string[],
    deleteStatus: boolean,
    table: MRT_TableInstance<UserResponse>,
    activationPeriod?: number
  ) => {
    try {
      const response = await dispatch(
        updateUserStatus({
          userIds: selectedRowIds,
          deleteStatus: deleteStatus,
          activationPeriod: activationPeriod,
        })
      ).unwrap();
      if (response.isSuccess) {
        handleResetAndRefresh(table);
        toast.success(response.message!, {
          toastId: "UpdateUsersStatusSuccess",
        });
      } else {
        toast.error(response.message!, {
          toastId: "UpdateUsersStatusError",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "UpdateUsersStatusException",
      });
    }
    return true;
  };

  const handleBulkUsersImport = async (
    validData: any[],
    invalidData: any[]
  ) => {
    try {
      const invalidFileData = invalidData.map((item) => ({
        ...item.data,
        error: item.error, // Add the 'Error' field to each item
      }));
      if (validData.length === 0) {
        prepareAndDownloadFile(invalidFileData, "errorUserCreation_res");
        return false;
      }
      const bulkUserData = BulkUserAddRequestModel(validData);
      const response = await dispatch(
        addBulkUsers({ bulkUserRequest: bulkUserData, fdpId: fdpId })
      ).unwrap();
      if (response.isSuccess) {
        if (invalidData.length) {
          prepareAndDownloadFile(invalidFileData, "errorUserCreation_res");
          toast.warning("Some FDPs couldn't save, Please check the file!", {
            toastId: "AddBulkFdpException",
          });
          return true;
        }
        toast.success(response.message!, {
          toastId: "BulkUserImportSuccess",
        });
      } else {
        if (response.data && response.data.length) {
          const requiredKeys = UserExpectedColumns.map(
            (item) => item.camelCaseName
          );
          requiredKeys.push("error");
          // Filter response data to keep only keys present in invalidFileData
          const filteredResponseData = response.data.map((item: any) => {
            const filteredItem: any = {};
            requiredKeys.forEach((key) => {
              if (item.hasOwnProperty(key)) {
                filteredItem[key] = item[key] ? item[key].toLocaleString() : "";
              }
            });
            return filteredItem;
          });
          // Combine invalidFileData and filteredResponseData
          const mergedData = [...invalidFileData, ...filteredResponseData];
          prepareAndDownloadFile(mergedData, "errorUserCreation_res");
          toast.error(response.message!, {
            toastId: "AddBulkFdpException",
          });
          return true;
        }
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "BulkUserImportSuccess",
      });
    }
    return true;
  };

  const handleDownloadMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const disableActivateButton = (table: MRT_TableInstance<UserResponse>) => {
    const selectedRows = table.getSelectedRowModel().flatRows;
    if (selectedRows.length === 0) {
      return true;
    }
    for (const row of selectedRows) {
      if (row.original.deleted === "false") {
        return true;
      }
    }
    return false;
  };

  const disableDeactivateButton = (table: MRT_TableInstance<UserResponse>) => {
    const selectedRows = table.getSelectedRowModel().flatRows;
    if (selectedRows.length === 0) {
      return true;
    }
    for (const row of selectedRows) {
      if (row.original.deleted === "true") {
        return true;
      }
    }
    return false;
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  // ***** UseMemos *****
  // Columns
  const columns = useMemo<MRT_ColumnDef<UserResponse>[]>(
    () => [
      {
        accessorKey: USER_COLUMN_NAME.USER_ID,
        header: USER_LABEL_NAME.USER_ID,
        size: 10,
      },
      {
        accessorKey: USER_COLUMN_NAME.USER_NAME,
        header: USER_LABEL_NAME.USER_NAME,
        size: 100,
      },
      {
        accessorKey: USER_COLUMN_NAME.FIRSTNAME,
        header: USER_LABEL_NAME.FIRSTNAME,
        size: 50,
      },
      {
        accessorKey: USER_COLUMN_NAME.LASTNAME,
        header: USER_LABEL_NAME.LASTNAME,
        size: 50,
      },
      {
        accessorKey: USER_COLUMN_NAME.USER_DOCS,
        header: USER_LABEL_NAME.USER_DOCS,
        size: 50,
        Cell: ({ row }) => {
          const userDocs = row.original.userRelatedDocs;
          return userDocs && userDocs.length > 0 ? (
            <>
              {userDocs.map((doc) => (
                <Box
                  key={doc.id}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <Typography sx={{ minWidth: "45px" }}>
                    {doc.fileType}
                  </Typography>
                  {doc.isUploaded ? (
                    <DoneIcon color="primary" />
                  ) : (
                    <CloseIcon color="error" />
                  )}
                </Box>
              ))}
            </>
          ) : (
            <span>N/A</span>
          );
        },
      },
      {
        accessorKey: USER_COLUMN_NAME.EMAIL,
        header: USER_LABEL_NAME.EMAIL,
        size: 150,
      },
      {
        accessorKey: USER_COLUMN_NAME.PHONE,
        header: USER_LABEL_NAME.PHONE,
        size: 50,
      },
      {
        accessorKey: USER_COLUMN_NAME.DELETED,
        header: USER_LABEL_NAME.DELETED,
        size: 10,
        Cell: ({ row }) => (
          <div>
            {row.original.deleted === "true" ? (
              <span>False</span>
            ) : (
              <span>True</span>
            )}
          </div>
        ),
      },
      {
        accessorKey: USER_COLUMN_NAME.ASSIGNED_ROLES,
        header: USER_LABEL_NAME.ASSIGNED_ROLES,
        Cell: ({ row }) => {
          const assignedRoles = row.original.assignedRoles;
          return assignedRoles && assignedRoles.length > 0 ? (
            <div>
              <UserRolesTooltip
                roles={assignedRoles.map((x) => x.roleName)}
              ></UserRolesTooltip>
            </div>
          ) : (
            <span>N/A</span>
          );
        },
        size: 50,
        enablePinning: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: USER_COLUMN_NAME.ASSIGNED_PARCEL_CENTERS,
        header: USER_LABEL_NAME.ASSIGNED_PARCEL_CENTERS,
        Cell: ({ cell, row }) => {
          const assignedPcs = row.original.assignedParcelCenters;
          return assignedPcs && assignedPcs.length > 0 ? (
            <div>
              <UserParcelCenterTooltip
                parcelCenters={row.original.assignedParcelCenters.map(
                  (x) => x.parcelCenterName
                )}
              ></UserParcelCenterTooltip>
            </div>
          ) : (
            <span>N/A</span>
          );
        },
        size: 20,
        enablePinning: false,
        enableColumnFilter: false,
      },
      {
        header: USER_LABEL_NAME.ONE_TIME_PASSWORD,
        Cell: ({ row }) => {
          return (
            <div
              style={{ display: "flex", justifyContent: "center" }}
              onClick={(e) => e.stopPropagation()}
            >
              <UserOneTimePasswordButton userInfo={row.original} />
            </div>
          );
        },
        size: 10,
        enablePinning: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: USER_COLUMN_NAME.CREATED_ON,
        header: USER_LABEL_NAME.CREATED_ON,
        size: 80,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.createdOn ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                  hourCycle: "h12",
                })}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
      {
        accessorKey: USER_COLUMN_NAME.UPDATED_ON,
        header: USER_LABEL_NAME.UPDATED_ON,
        size: 200,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.updatedOn ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                  hourCycle: "h12",
                })}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
    ],
    []
  );
  const handleXlsxFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      const fileData = getFilteredDataFileMapping();
      // Convert data to worksheet
      const worksheet = XLSX.utils.json_to_sheet(fileData);
      // Create a workbook and add the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "FdpUsersData");
      // Convert the workbook to a binary XLSX object
      const wbArray = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbArray], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      // Download the XLSX file
      downloadXlsx(blob, "FdpUserData.xlsx");
    } catch (error) {
      toast.error("unable to download file", {
        toastId: "FdpUserDownloadXlsx",
      });
    }
  };

  const handleCsvFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      // Transform tableData into CSV format
      const csvData = getFilteredDataFileMapping();
      // Convert CSV data to CSV string
      const csvContent = convertArrayToCSV(csvData);
      // Download the CSV
      const file: DownloadFileModel = {
        data: csvContent,
        contentType: "text/csv",
        fileName: "FdpUserData.csv",
      };
      downloadCsv(file);
    } catch (error) {
      toast.error("unable to download file", {
        toastId: "FdpuserDownloadCsv",
      });
    }
  };
  const getFilteredDataFileMapping = () => {
    return tableData.map((item: UserResponse) => {
      const parcelCenters = item.assignedParcelCenters
        ? `"${item.assignedParcelCenters
            .map((parcel) => parcel.parcelCenterName)
            .join(", ")}"`
        : "N/A";
      const roles = item.assignedRoles
        ? `"${item.assignedRoles.map((role) => role.roleName).join(", ")}"`
        : "N/A";
      const activeStatus = item.deleted === "true" ? "False" : "True";
      return {
        UserName: item.userName,
        FirstName: item.firstName,
        LastName: item.lastName,
        Email: item.email,
        Phone: item.phone,
        ActiveStatus: activeStatus,
        Roles: roles,
        ParcelCenters: parcelCenters,
        CreatedOn: item.createdOn,
        UpdatedOn: item.updatedOn,
      };
    });
  };
  // ***** Modal Popups *****
  const [editUserModal, openEditUserModal] =
    useSubmitModalWithParameter<UserResponse>(
      (fdpUserRowData, closeEditUserModal) => (
        <EditUserModal
          closeEditUserModal={closeEditUserModal}
          onCompleted={handleRefresh}
          userRowData={fdpUserRowData}
          isForFdps={true}
        />
      )
    );
  const [addUserModal, openAddUserModal] = useSubmitModal(
    (closeAddUserModal) => (
      <AddUserModal
        closeAddUserModal={closeAddUserModal}
        onCompleted={handleRefresh}
        requestingFdpId={fdpRowData.id}
        isForFdps={true}
      />
    )
  );

  const [fileUploadModal, openFileUploadModal] = useSubmitModal(
    (closeFileUploadModal) => (
      <FileUploadModal
        closeFileUploadModal={closeFileUploadModal}
        expectedColumns={UserExpectedColumns}
        asyncSubmitOperation={handleBulkUsersImport}
        onCompleted={handleRefresh}
        sampleFileName={"Bulk_User_upload_Sample.xlsx"}
      />
    )
  );

  const handleModalClose = () => {
    setOpenUserActivationPropmtModal(false);
  };

  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<UserResponse>
  ) => {
    return (
      <Box display="flex" alignContent="center">
        <CustomTooltip arrow title="Refresh">
          <IconButton onClick={() => handleResetAndRefresh(table)}>
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip arrow title="Upload/Import">
          <IconButton type="button" onClick={openFileUploadModal}>
            <UploadIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip arrow title="Download/Export">
          <span>
            <IconButton
              disabled={tableData.length === 0}
              type="button"
              onClick={handleDownloadMenuClick}
            >
              <DownloadIcon sx={{ color: "primary.main" }} />
            </IconButton>
          </span>
        </CustomTooltip>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem onClick={(event) => handleCsvFileDownload(event)}>
            CSV
          </MenuItem>
          <MenuItem onClick={(event) => handleXlsxFileDownload(event)}>
            EXCEL
          </MenuItem>
        </Menu>
        {IsCreatePermission && IsFdcAdmin && (
          <CustomTooltip arrow title="New User">
            <span>
              <IconButton
                disabled={isFdpLoading}
                type="button"
                onClick={openAddUserModal}
              >
                <AddIcon sx={{ color: "primary.main" }} />
              </IconButton>
            </span>
          </CustomTooltip>
        )}
        {IsCreatePermission && IsFdcAdmin && (
          <>
            <Button
              color="success"
              disabled={disableActivateButton(table)}
              onClick={() => handleUserActivationValidation(table)}
              variant="contained"
              size="small"
              sx={{ ml: 1 }}
            >
              ACTIVATE
            </Button>
            {openUserActivationPropmtModal && (
              <UserActivationPrompt
                isOpen={openUserActivationPropmtModal}
                closeUserActivationModal={handleModalClose}
                table={table}
                asyncOnSubimtFunction={handleDeleteStatus}
              />
            )}
          </>
        )}
        {IsCreatePermission && IsFdcAdmin && (
          <Button
            color="warning"
            disabled={disableDeactivateButton(table)}
            onClick={() => handleDeleteStatus(table, true)}
            variant="contained"
            size="small"
            sx={{ ml: 1 }}
          >
            DEACTIVATE
          </Button>
        )}
        {IsCreatePermission && IsFdcAdmin && (
          <FormControlLabel
            title="Switch Users"
            control={
              <Switch
                size="small"
                checked={isActiveUserView}
                onChange={() => setActiveUserView(!isActiveUserView)}
              />
            }
            label={isActiveUserView ? "Active User" : ""}
            sx={{ ml: 1 }}
            labelPlacement="end"
          />
        )}
      </Box>
    );
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <CommandOpsDataGrid
          data={isActiveUserView ? activeUsers : tableData}
          columns={columns}
          isLoading={isFdpLoading || isTableLoading || isLoading}
          renderTopToolbarCustomActions={renderTopToolbarCustomActions}
          emptyRowMessage={"No FDP users"}
          rowCount={tableDataCount}
          isDescending={false}
          defaultSortColumnName="userName"
          openModal={openEditUserModal}
          checkBoxSelection={true}
          setSelectedRows={setSelectedRows}
        />
        {addUserModal}
        {editUserModal}
        {fileUploadModal}
      </Box>
    </>
  );
};

export default FdpUsers;
